import { Client, IMessage, IFrame } from "@stomp/stompjs";
import { getRegisterDeviceOptionsForWebsocket } from "../helper/register-device";
import { MessagePayload, UUID } from "../../types";
import { CONNECTION_FAILED } from "../config/constants";
import * as Sentry from "@sentry/browser";
import LocalStorage from "./local-storage";
import StorageConstants from "src/config/storage.constants";
import { isAuthRejection } from "./error";
import { checkBackendHealth } from "../../services/health.service";

export interface WebSocketClientOptions {
  url: string;
  debug?: (message: string) => void;
  authToken?: string | null;
  userId: UUID;
  deviceId: UUID;
}

export type OnRegisterDeviceCallback = (isDeviceRegistered: boolean) => void;
export type OnMessageCallback = (data: any) => void;
export type OnErrorCallback = (error: {
  message: string;
  type: "STOMP" | "WEBSOCKET" | "GENERIC";
}) => void;

export default class WebSocketClient {
  private client: Client | null = null;
  private authToken: string | null = null;
  private userId: UUID | null = null;
  private deviceId: UUID;
  private onRegisterDeviceCallback: OnRegisterDeviceCallback | null = null;
  private onMessageCallback: OnMessageCallback | null = null;
  private onErrorCallback: OnErrorCallback | null = null;

  constructor({
    url,
    authToken = null,
    userId,
    deviceId,
    debug = () => {},
  }: WebSocketClientOptions) {
    this.userId = userId || null; // Set userId to null if it is null or undefined
    this.authToken = authToken;
    this.deviceId = deviceId;

    this.client = new Client({
      brokerURL: url,
      debug: (message) => {
        debug(`${new Date().toUTCString()} | ${message}`);
      },
      reconnectDelay: 2000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      connectHeaders: this.getHeaders(),
    });

    this.client.beforeConnect = () => {
      if (this.client) {
        this.client.connectHeaders = this.getHeaders();
      }
    };
  }

  onMessage(onMessageCallback: OnMessageCallback) {
    this.onMessageCallback = onMessageCallback;
  }

  onError(onErrorCallback: OnErrorCallback) {
    this.onErrorCallback = onErrorCallback;
  }

  onRegisterDevice(onRegisterDeviceCallback: OnRegisterDeviceCallback) {
    this.onRegisterDeviceCallback = onRegisterDeviceCallback;
  }

  private registerDevice() {
    if (!this.client || !this.userId || !this.deviceId) {
      this.onErrorCallback?.({ message: CONNECTION_FAILED, type: "GENERIC" });
      return;
    }

    if (this.client.connected) {
      this.client.publish({
        destination: "/app/registerDesktopDevice",
        body: JSON.stringify(
          getRegisterDeviceOptionsForWebsocket({
            userId: this.userId,
            deviceId: this.deviceId,
          }),
        ),
      });

      this.onRegisterDeviceCallback?.(true);
    }
  }

  private async handleUnexpectedDisconnect(
    closeData?: {
      code: number;
      wasClean: boolean;
    },
    errorReason?: string,
  ) {
    // Check if backend itself is down
    let isBackendUp = true;
    const isOnline = navigator.onLine;

    if (isOnline) {
      isBackendUp = await checkBackendHealth();
    }

    if (closeData) {
      Sentry.captureMessage(errorReason || "Web Socket connection closed", {
        tags: {
          userId: this.userId,
          connectionCloseCode: closeData.code,
          cleanClosed: closeData.wasClean,
          isConvoAPIServerDown: !isBackendUp,
        },
      });
    }
  }

  connect() {
    if (!this.client) {
      this.onErrorCallback?.({ message: CONNECTION_FAILED, type: "GENERIC" });
      return;
    }

    this.client.activate();

    this.client.onWebSocketError = () => {
      this.onErrorCallback?.({ message: CONNECTION_FAILED, type: "WEBSOCKET" });
    };

    this.client.onWebSocketClose = (data) => {
      if (data.code !== 1000) {
        this.handleUnexpectedDisconnect(data);
      }
    };

    this.client.onStompError = (frame: IFrame) => {
      let serverErrorMessage = frame.headers.message || "STOMP protocol error";

      // 1. Sanitize the carriage return artifacts
      serverErrorMessage = serverErrorMessage.replace(/\\c/g, "").trim();

      console.error(
        "STOMP Protocol Error Received From Backend:",
        serverErrorMessage,
      );

      // 2. Notify the orchestrator (Messaging) so it can decide Sentry / redirect.
      this.onErrorCallback?.({ message: serverErrorMessage, type: "STOMP" });

      // 3. Auth rejections are expected: skip Sentry. Anything else is an anomaly.
      if (!isAuthRejection(serverErrorMessage)) {
        this.handleUnexpectedDisconnect(
          { code: 4001, wasClean: false },
          `STOMP Error: ${serverErrorMessage}`,
        );
      }
    };

    this.client.onConnect = () => {
      this.subscribe();

      this.registerDevice();
    };
  }

  private getHeaders() {
    const isHUUser = Boolean(LocalStorage.get(StorageConstants.IS_HU_USER));
    const headers: Record<string, string> = {
      "App-Name": "userapp",
      "App-Source": isHUUser ? "huqrapp" : "qrapp",
    };

    if (this.authToken) {
      headers.Authorization = this.authToken.startsWith("Bearer ")
        ? this.authToken
        : `Bearer ${this.authToken}`;
    }

    return headers;
  }

  private handleSubscriptionMessage(message: IMessage) {
    const { body } = message;

    if (body) {
      try {
        const messagePayload = JSON.parse(body) as MessagePayload;
        if (messagePayload?.data) {
          this.onMessageCallback?.(messagePayload.data);
        }
      } catch (e) {
        console.error("Failed parsing message frame payload", e);
      }
    }
  }

  handleUnrecoverableStompError(serverErrorMessage: string) {
    Sentry.captureMessage(`Unrecoverable STOMP Error: ${serverErrorMessage}`, {
      level: "error",
      tags: {
        userId: this.userId,
        unrecoverableStompError: true,
      },
    });

    this.forceShutdown();
  }

  private subscribe() {
    if (this.client && this.userId && this.deviceId) {
      this.client.subscribe(
        `/user/${this.userId}/${this.deviceId}/notification`,
        (message) => this.handleSubscriptionMessage(message),
      );
    }
  }

  /**
   * Hard-stops the client after an expected, non-recoverable rejection (e.g. auth
   * failure): kills auto-reconnect and unregisters the STOMP callbacks so nothing
   * fires during teardown.
   */
  forceShutdown() {
    if (this.client) {
      // Kill the auto-reconnect delay ONLY for this specific forced shutdown
      this.client.reconnectDelay = 0;

      this.client.onConnect = () => {};
      this.client.onStompError = () => {};
      this.client.onWebSocketClose = () => {};
      this.client.onWebSocketError = () => {};
      this.client.beforeConnect = () => {};

      this.client.deactivate({ force: true });
    }

    this.client = null;
    this.userId = null;
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onRegisterDeviceCallback = null;
  }

  destroy() {
    if (this.client) {
      this.client.onConnect = () => {};
      this.client.onStompError = () => {};
      this.client.onWebSocketClose = () => {};
      this.client.onWebSocketError = () => {};
      this.client.beforeConnect = () => {};

      this.client.deactivate({ force: true });
    }

    this.client = null;
    this.userId = null;
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onRegisterDeviceCallback = null;
  }
}
